import React, { useState } from 'react'

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  Stack,
} from '@mui/material'
import { tsGetAnimalsInContainer } from '../../../Typescript/API_Response'
import { useAddSampleContext } from '../../../Contexts/Modules/SampleModule/AddSampleModule'
import { ReadOnlyTextField } from '../../../garvan-react/Components/ReadOnlyTextField'
import LineInstructions from './LineInstructions'
import PageSection from '../../../Constants&Theme/PageSection'
import AnimalCard from './Cards/AnimalCard'
import SlowDownDialog from './SlowDownDialog'
import {
  tsAddAnimal,
  tsStorageAnimalMeta,
  validSamplesState,
} from './ValidSamplesState'
import { useSaveSamples } from '../../../APIs/Hooks/SampleHooks'
import { useGlobalContext } from '../../../Contexts/GlobalTriggers'
import { onScanFn } from './OnScanFn'
import { useUserContext } from '../../../Contexts/UserConfig'
import { stackGap } from '../../../Constants&Theme/Spacing'
import StorageCard from './Cards/StorageCard'
import { useSampleContext } from '../../../Contexts/Modules/SampleModule/SampleModule'
import { listFromKV } from '../../../Functions/ListItemGenerator'
import InvalidAccessAlert from './InvalidAccessAlert'
import { initAddAnimals } from './Functions/initAddAnimals'

/**
 * Props:
 * - qty: 'boolean' | 'multiple'
 *   - 'boolean': each animal has a single yes/no collection with a single Sample ID field
 *   - 'multiple': each animal can have multiple sample entries with storage metadata
 */
function AddAnimalSamples({ qty }: { qty: 'boolean' | 'multiple' }) {
  const { whoAmI } = useUserContext()
  const { playScanSound } = useGlobalContext()
  const { collection, sampleRun } = useAddSampleContext()
  const isRunType570 = sampleRun?.runInfo?.RunType === '570' || false
  const animalCollection = collection as tsGetAnimalsInContainer
  const [animals, setAnimals] = useState<tsAddAnimal>(
    initAddAnimals(animalCollection?.animals ?? []),
  )
  const [containerMeta, setContainerMeta] = useState<
    Partial<tsStorageAnimalMeta>
  >({})

  const [lastScan, setLastScan] = useState(0)
  const [showTimeout, setShowTimeout] = useState<null | number>(null)
  const { mutate: saveSamples } = useSaveSamples(
    animals,
    setAnimals,
    containerMeta,
    setContainerMeta,
  )

  // Handler passed to child inputs. When user presses Enter/Tab, validate and advance focus.
  function onSampleEnterTab(e: any, _index: number, sampleIndex: number = 0) {
    // Compute absolute input cursor among all Sample ID inputs so focus advances correctly even with multiple samples per animal
    const inputs: HTMLInputElement[] = Array.prototype.slice
      .call(
        document.querySelectorAll(
          'input.MuiInputBase-input.MuiOutlinedInput-input',
        ),
      )
      .filter((item: any) => item?.attributes?.valid)
    // Find the absolute index of the current target within these inputs
    let currentCursor = Math.max(0, inputs.indexOf(e.target))
    onScanFn(
      e,
      whoAmI,
      lastScan,
      setLastScan,
      setShowTimeout,
      animals,
      setAnimals,
      playScanSound,
      currentCursor,
      sampleIndex,
      qty,
    )
  }

  // Preload selectable codes for euthanasia when needed
  const { euthanasiaCodesQuery } = useSampleContext()
  if (Object.keys(animals).length < 1) {
    return <InvalidAccessAlert />
  }
  return (
    <Box
      sx={{
        maxWidth: 'md',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <LineInstructions />
      <PageSection title={'Animal Samples'}>
        <Stack
          gap={stackGap.col}
          width={1}
        >
          <Stack
            gap={stackGap.row}
            width={1}
            direction={'row'}
          >
            <ReadOnlyTextField
              label="Container"
              value={`${animalCollection?.container?.ContainerID}`}
              fullWidth
            />
            <ReadOnlyTextField
              label="Zone Name"
              value={`${animalCollection?.container?.ZoneName}`}
              fullWidth
            />
            <ReadOnlyTextField
              label="Owner Name"
              value={`${animalCollection?.container?.OwnerName}`}
              fullWidth
            />
            {/* In multi-sample flows (non-570), require selecting euthanasia method at container level */}
            {qty === 'multiple' && !isRunType570 && (
              <FormControl
                fullWidth
                required
              >
                <InputLabel>Euthanasia</InputLabel>
                <Select
                  label="Euthanasia"
                  value={containerMeta?.MethodEuthanasia ?? ''}
                  onChange={(e) => {
                    setContainerMeta((m) => {
                      return {
                        ...m,
                        MethodEuthanasia: e.target.value,
                      }
                    })
                  }}
                >
                  {listFromKV(
                    euthanasiaCodesQuery.data ?? [],
                    euthanasiaCodesQuery.isLoading,
                  )}
                </Select>
              </FormControl>
            )}
          </Stack>
          {animalCollection?.animals.map((animal, index) => {
            if (qty === 'multiple') {
              return (
                <StorageCard
                  animal={animal}
                  setAnimals={setAnimals}
                  animals={animals}
                  animalIndex={index}
                  key={index}
                  onSampleEnterTab={onSampleEnterTab}
                  containerMeta={containerMeta}
                />
              )
            } else {
              return (
                <AnimalCard
                  animal={animal}
                  setAnimals={setAnimals}
                  animals={animals}
                  index={index}
                  key={index}
                  onSampleEnterTab={onSampleEnterTab}
                />
              )
            }
          })}
          <Button
            fullWidth
            variant="contained"
            color="success"
            disabled={
              !validSamplesState(
                animals,
                animalCollection,
                qty === 'multiple',
                containerMeta,
                isRunType570,
              )
            }
            onClick={() => saveSamples()}
          >
            Save Samples
          </Button>
        </Stack>
      </PageSection>
      <SlowDownDialog
        open={showTimeout}
        lastScan={lastScan}
      />
    </Box>
  )
}

export default AddAnimalSamples
